'use client';
import { useState, useEffect, useMemo } from 'react';
import type { FilterGroup } from '@practium/ui';
import { mapListPageLineChart, mapListPagePieData } from '@practium/features/dashboard';
import type { UsersPermissions } from '../types';
import { useGetUsers } from '../../api/hooks/users/useGetUsers';
import { useGetUsersStats } from '../../api/hooks/users/useGetUsersStats';
import { useDebounce } from '../../../shared/utils/useDebounce';

const PAGE_SIZE = 10;

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Status',
		options: [
			{ value: 'status:true', label: 'Ativo' },
			{ value: 'status:false', label: 'Inativo' },
		],
	},
];

export interface UseUsersPageProps {
	permissions: UsersPermissions;
}

export const useUsersPage = ({ permissions }: UseUsersPageProps) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [page, setPage] = useState(1);

	const debouncedSearch = useDebounce(searchQuery, 400);
	const { data: chartsData, isLoading: isChartsLoading } = useGetUsersStats();

	const statusFilter = filterValues
		.filter(v => v.startsWith('status:'))
		.map(v => v.slice(7) === 'true')[0];

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, filterValues, sortAsc]);

	const { data: filteredUsers, pagination, isLoading } = useGetUsers({
		page,
		limit: PAGE_SIZE,
		userNameOrEmail: debouncedSearch,
		status: statusFilter,
	});

	const profilesPieData = useMemo(
		() => mapListPagePieData(chartsData?.pie?.items),
		[chartsData]
	);

	const {
		lineData: insertionsLineData,
		mainValue: chartMainValue,
		trendValue: chartTrendValue,
		trendDirection: chartTrendDirection,
		footerText: insertionsFooterText,
	} = useMemo(() => mapListPageLineChart(chartsData?.line), [chartsData]);

	return {
		filteredUsers,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc(v => !v),
		filterValues,
		setFilterValues,
		filterGroups: FILTER_GROUPS,
		page,
		setPage,
		totalPages: pagination?.totalPages ?? 1,
		isLoading: isLoading || isChartsLoading,
		permissions,
		profilesPieData,
		insertionsLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		insertionsFooterText,
	};
};
